import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import {
  CircleNotch,
  CheckCircle,
  Warning,
  CurrencyDollar,
} from "@phosphor-icons/react";

/**
 * StripePricingDiagnostic — pings /api/billing/diagnose and renders
 * the live Stripe-resolved prices + any misconfig warnings. The whole
 * point: if `STRIPE_PRICE_MONTHLY` ever silently points at the legacy
 * $5 ID again (Feb 2026 incident), this tile screams the moment an
 * admin loads the dashboard instead of waiting on a customer to find
 * it via a $5 checkout receipt.
 */
function PriceRow({ label, info }) {
  if (!info?.configured) {
    return (
      <div className="flex items-center justify-between text-sm py-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-xs italic text-muted-foreground">Not configured</span>
      </div>
    );
  }
  if (info.error) {
    return (
      <div className="flex items-start justify-between text-sm py-1 gap-3">
        <span className="text-muted-foreground flex-none">{label}</span>
        <span className="text-xs text-rose-600 text-right font-mono leading-snug" title={info.error}>
          {info.error}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-bold text-base">{info.amount_display || "—"}</span>
        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
          {info.interval ? `/ ${info.interval}` : ""}
        </span>
        {info.active === false && (
          <span className="text-[10px] font-semibold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">
            inactive
          </span>
        )}
      </div>
    </div>
  );
}

export default function StripePricingDiagnostic() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/billing/diagnose");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e) || "Could not load Stripe diagnostic");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const warnings = data?.warnings || [];
  const anyErrors = data ? Object.values(data.prices || {})
    .some((p) => p?.configured && p?.error) : false;
  const ok = data && warnings.length === 0 && !anyErrors;

  return (
    <div
      className={
        "rounded-2xl border p-5 " +
        (ok
          ? "border-emerald-200 bg-emerald-50/30 dark:border-emerald-500/30 dark:bg-emerald-500/5"
          : warnings.length > 0
          ? "border-rose-300 bg-rose-50/40 dark:border-rose-500/30 dark:bg-rose-500/5"
          : "border-border bg-card")
      }
      data-testid="admin-stripe-diagnostic"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <CurrencyDollar weight="duotone" className="h-5 w-5 text-primary" />
            Stripe pricing
            {ok && (
              <CheckCircle weight="fill" className="h-4 w-4 text-emerald-500" />
            )}
            {warnings.length > 0 && (
              <Warning weight="fill" className="h-4 w-4 text-rose-500" />
            )}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Live-resolved from Stripe. If anything here is wrong, customers will be billed at the wrong rate.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-stripe-refresh"
        >
          {loading ? <CircleNotch className="h-3 w-3 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {err && (
        <p className="text-sm text-rose-600 py-2" data-testid="admin-stripe-error">
          {err}
        </p>
      )}

      {data && (
        <>
          {warnings.length > 0 && (
            <div className="mb-3 rounded-lg bg-rose-100 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/30 p-3" data-testid="admin-stripe-warnings">
              <div className="text-xs font-bold text-rose-800 dark:text-rose-300 uppercase tracking-wide mb-1">
                Misconfiguration detected
              </div>
              <ul className="text-sm text-rose-900 dark:text-rose-200 space-y-1">
                {warnings.map((w, i) => (
                  <li key={i} className="leading-snug">{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="divide-y divide-border/50">
            <div className="flex items-center justify-between text-sm py-1">
              <span className="text-muted-foreground">Secret key mode</span>
              <span
                className={
                  "font-bold uppercase text-xs tracking-wider px-2 py-0.5 rounded " +
                  (data.secret_key_mode === "live"
                    ? "bg-emerald-100 text-emerald-700"
                    : data.secret_key_mode === "test"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-rose-100 text-rose-700")
                }
              >
                {data.secret_key_mode}
                {data.env_live?.STRIPE_SECRET_KEY_prefix
                  ? ` · ${data.env_live.STRIPE_SECRET_KEY_prefix}`
                  : ""}
              </span>
            </div>
            <PriceRow label="Monthly (current)"   info={data.prices?.current_monthly} />
            <PriceRow label="Monthly (legacy $5)" info={data.prices?.legacy_monthly} />
            <PriceRow label="Annual"              info={data.prices?.annual} />
            <div className="flex items-center justify-between text-sm py-1">
              <span className="text-muted-foreground">Free trial</span>
              <span className={"font-semibold " + (data.trial_days > 14 ? "text-amber-700" : "")}>
                {data.trial_days ? `${data.trial_days} days` : "off"}
                {data.env_live?.STRIPE_TRIAL_DAYS != null
                  && data.env_live.STRIPE_TRIAL_DAYS !== "(unset)"
                  && String(data.trial_days) !== String(data.env_live.STRIPE_TRIAL_DAYS) && (
                  <span className="ml-2 text-[10px] font-normal text-rose-600 block sm:inline">
                    (env var says {data.env_live.STRIPE_TRIAL_DAYS} — backend hasn&apos;t restarted)
                  </span>
                )}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
